import { formatMoney } from '@theme/utilities';

export function shippingProgress({ total, thresholdUsd, currency, shopCurrency, activeCurrency, rate }) {
  const subtotal = Number(total);
  const baseThreshold = Number(thresholdUsd);
  if (!Number.isFinite(subtotal) || subtotal < 0 || !Number.isFinite(baseThreshold) || baseThreshold <= 0) return null;
  let exchangeRate = 1;
  if (currency !== 'USD') {
    if (shopCurrency !== 'USD' || activeCurrency !== currency) return null;
    exchangeRate = Number(rate);
    if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) return null;
  }
  let fractionDigits;
  try {
    fractionDigits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
  } catch {
    return null;
  }
  const quantum = fractionDigits === 0 ? 100 : 1;
  const threshold = Math.ceil((baseThreshold * exchangeRate) / quantum - 1e-9) * quantum;
  const remaining = Math.max(0, threshold - subtotal);
  return { subtotal, threshold, remaining, progress: Math.min(subtotal, threshold), reached: remaining === 0 };
}

let conversionFrame;
function reconvertMoney() {
  if (conversionFrame) return;
  conversionFrame = requestAnimationFrame(() => {
    conversionFrame = null;
    window.bucksCC?.reConvert?.();
  });
}

export function renderMoneyTemplate(element, template, amounts, currency = 'USD') {
  const fragment = document.createDocumentFragment();
  const tokens = template.split(/(\[(?:amount|subtotal|threshold)\])/);
  for (const token of tokens) {
    const key = token.slice(1, -1);
    if (Object.hasOwn(amounts, key) && token === '[' + key + ']') {
      const holder = document.createElement('span');
      if (currency === 'USD') {
        holder.innerHTML = formatMoney(amounts[key]);
        if (!holder.querySelector('.money')) holder.className = 'money';
      } else {
        holder.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency, currencyDisplay: 'code'
        }).format(amounts[key] / 100);
      }
      fragment.append(holder);
    } else {
      fragment.append(document.createTextNode(token));
    }
  }
  element.replaceChildren(fragment);
}

if (typeof document !== 'undefined') {
  for (const eventName of ['currency:update', 'cart:drawer:render', 'cart:main:render']) {
    document.addEventListener(eventName, reconvertMoney);
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('avensai-shipping-progress')) {
  customElements.define('avensai-shipping-progress', class extends HTMLElement {
    static get observedAttributes() { return ['data-total', 'data-currency', 'data-threshold-usd', 'data-first-order']; }

    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.moneyObserver?.disconnect();
      this.moneyObserver = new MutationObserver(() => this.syncAccessibleMessage());
      const message = this.querySelector('[data-shipping-message]');
      if (message) this.moneyObserver.observe(message, { childList: true, characterData: true, subtree: true });
      this.render();
      document.addEventListener('currency:update', () => this.render(), { signal: this.controller.signal });
      window.addEventListener('pageshow', () => this.render(), { signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.moneyObserver?.disconnect();
    }
    attributeChangedCallback() { if (this.isConnected) this.render(); }

    syncAccessibleMessage() {
      const bar = this.querySelector('[data-shipping-bar]');
      const message = this.querySelector('[data-shipping-message]');
      if (bar && message && !bar.hidden) bar.setAttribute('aria-valuetext', message.textContent.trim());
    }

    render() {
      const message = this.querySelector('[data-shipping-message]');
      const bar = this.querySelector('[data-shipping-bar]');
      if (!message) return;
      if (this.dataset.firstOrder === 'true') {
        message.textContent = this.dataset.reachedText;
        this.dataset.reached = 'true';
        if (bar) {
          bar.max = 1;
          bar.value = 1;
          bar.hidden = false;
        }
        this.syncAccessibleMessage();
        this.renderSignature = null;
        return;
      }
      const result = shippingProgress({
        total: this.dataset.total,
        thresholdUsd: this.dataset.thresholdUsd,
        currency: this.dataset.currency,
        shopCurrency: this.dataset.shopCurrency,
        activeCurrency: window.Shopify?.currency?.active,
        rate: window.Shopify?.currency?.rate
      });
      if (!result) {
        message.textContent = this.dataset.fallbackText;
        if (bar) bar.hidden = true;
        this.renderSignature = null;
        this.removeAttribute('data-reached');
        return;
      }
      const signature = JSON.stringify([this.dataset.currency, this.dataset.remainingTemplate, this.dataset.reachedText, result]);
      if (signature === this.renderSignature) return;
      this.renderSignature = signature;
      if (result.reached) message.textContent = this.dataset.reachedText;
      else renderMoneyTemplate(message, this.dataset.remainingTemplate, {amount: result.remaining}, this.dataset.currency);
      this.dataset.reached = String(result.reached);
      if (bar) {
        bar.max = result.threshold;
        bar.value = result.progress;
        bar.hidden = false;
      }
      this.syncAccessibleMessage();
      reconvertMoney();
    }
  });
}

